import React, { useState, useMemo, useEffect } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FilterListIcon from "@mui/icons-material/FilterList";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Tooltip from "@mui/material/Tooltip";
import { NumericFormat } from "react-number-format";
import { tokens } from "../../Common/designTokens";
import FilterChips from "../InventoryComponents/FilterChips";
import {
  WATCHLIST_FILTER_BOND_TYPES,
  WATCHLIST_FILTER_FIELDS,
  WATCHLIST_FILTER_LABELS,
  WATCHLIST_FILTER_FIELD_OPTIONS,
} from "./watchlistFilterOptions";

const API_TYPE_TO_BOND_TYPE = {
  Gov: "Treasury",
  GOV: "Treasury",
  Muni: "Municipals",
  MUNI: "Municipals",
  Corp: "Corporates",
  CORP: "Corporates",
};

const FILTER_INPUT_HEIGHT = 36;
const FILTER_COLLAPSED_MAX_HEIGHT = 170;
const WATCHLIST_ISSUER_MIN_SEARCH_LENGTH = 2;
const WATCHLIST_ISSUER_MAX_RESULTS = 200;
const FILTER_FIELD_ICON_CONFIG = {
  edgeOffset: 4,
  gap: 0,
  clearSize: 16,
  dropdownSize: 18,
  emptyReserve: 24,
  filledReserve: 27,
};

const getFilterIconReserve = (hasClear) => {
  return hasClear
    ? FILTER_FIELD_ICON_CONFIG.filledReserve
    : FILTER_FIELD_ICON_CONFIG.emptyReserve;
};

const getDropdownLeftOfClearOffset = () => (
  FILTER_FIELD_ICON_CONFIG.edgeOffset
  + FILTER_FIELD_ICON_CONFIG.clearSize
  + FILTER_FIELD_ICON_CONFIG.gap
);

const getFilterOptionValue = (option) => {
  if (typeof option === "object" && option !== null) {
    return option.rating ?? option.value ?? option.name ?? option.label ?? "";
  }

  return option;
};

const getFilterOptionLabel = (option) => {
  if (typeof option === "object" && option !== null) {
    return option.label ?? option.name ?? option.rating ?? option.value ?? "";
  }

  return option;
};

const getSelectedOptionLabel = (options, selectedValue) => {
  const selectedOption = options.find(
    (option) => `${getFilterOptionValue(option)}` === `${selectedValue}`
  );

  return selectedOption ? getFilterOptionLabel(selectedOption) : selectedValue;
};

const watchlistIssuerFilterOptions = createFilterOptions({
  ignoreCase: true,
  ignoreAccents: true,
  limit: WATCHLIST_ISSUER_MAX_RESULTS,
  stringify: (option) => `${option ?? ""}`,
  trim: true,
});

const filterInputBaseSx = {
  backgroundColor: tokens.colors.white,
  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
  borderRadius: "8px",
  height: `${FILTER_INPUT_HEIGHT}px`,
  padding: "0 10px !important",
  fontSize: "0.85rem",
  color: tokens.colors.fontColour,
  "& input": {
    padding: "0 !important",
    fontSize: "0.85rem",
    color: tokens.colors.fontColour,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

const filterSelectSx = {
  backgroundColor: tokens.colors.white,
  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
  borderRadius: "8px",
  height: `${FILTER_INPUT_HEIGHT}px`,
  fontSize: "0.85rem",
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
  "& .MuiSelect-select": {
    padding: "6px 10px !important",
    display: "flex",
    alignItems: "center",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

const filterLabelSx = {
  fontSize: "0.78rem",
  fontWeight: 600,
  color: tokens.colors.fontColour85,
  mb: 0.5,
  textTransform: "uppercase",
  letterSpacing: "0.03em",
};

const getNumericLimits = (name) => {
  if (name.includes("coupon")) return { min: 0, max: 100 };
  if (name.includes("price")) return { min: 0, max: 150 };
  if (name.includes("yield")) return { min: -100, max: 100 };
  if (name.includes("maturity")) return { min: 0, max: 100 };
  if (name === "duration") return { min: 0, max: 50 };
  return { min: -Infinity, max: Infinity };
};

const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};

const getRangeError = (filterData, minName, maxName) => {
  const min = toNum(filterData[minName]);
  const max = toNum(filterData[maxName]);
  if (min !== null && max !== null && min > max) {
    return "From value cannot be greater than To value.";
  }
  return null;
};

const errorInputSx = {
  border: `1px solid ${tokens.palette.danger}`,
  "&:hover": { border: `1px solid ${tokens.palette.danger}` },
};

const FieldLabelWithError = ({ label, error }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
    <Typography sx={{ ...filterLabelSx, mb: 0 }}>{label}</Typography>
    {error ? (
      <Tooltip title={error} arrow placement="top">
        <ErrorOutlineIcon sx={{ fontSize: 14, color: tokens.palette.danger }} />
      </Tooltip>
    ) : null}
  </Box>
);

const NumericRangeField = ({ label, minName, maxName, filterData, onChange, suffix, error }) => {
  const minInputSx = { ...filterInputBaseSx, ...(error ? errorInputSx : {}) };
  const maxInputSx = { ...filterInputBaseSx, ...(error ? errorInputSx : {}) };
  return (
    <Box>
      <FieldLabelWithError label={label} error={error} />
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <NumericFormat
          customInput={TextField}
          variant="standard"
          thousandSeparator=","
          value={filterData[minName] ?? ""}
          onValueChange={(values) => onChange(minName, values.floatValue ?? "")}
          placeholder="Min"
          InputProps={{ disableUnderline: true, sx: minInputSx }}
          sx={{ flex: 1, minWidth: 0 }}
          decimalScale={5}
          isAllowed={(values) => {
            const { floatValue } = values;
            const { min, max } = getNumericLimits(minName);
            return floatValue === undefined || (floatValue >= min && floatValue <= max);
          }}
        />
        {suffix && (
          <Typography sx={{ fontSize: "0.8rem", color: tokens.colors.fontColour85, flexShrink: 0 }}>
            {suffix}
          </Typography>
        )}
        <Typography sx={{ fontSize: "0.8rem", color: tokens.colors.fontColour50, flexShrink: 0 }}>–</Typography>
        <NumericFormat
          customInput={TextField}
          variant="standard"
          thousandSeparator=","
          value={filterData[maxName] ?? ""}
          onValueChange={(values) => onChange(maxName, values.floatValue ?? "")}
          placeholder="Max"
          InputProps={{ disableUnderline: true, sx: maxInputSx }}
          sx={{ flex: 1, minWidth: 0 }}
          decimalScale={5}
          isAllowed={(values) => {
            const { floatValue } = values;
            const { min, max } = getNumericLimits(maxName);
            return floatValue === undefined || (floatValue >= min && floatValue <= max);
          }}
        />
        {suffix && (
          <Typography sx={{ fontSize: "0.8rem", color: tokens.colors.fontColour85, flexShrink: 0 }}>
            {suffix}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const SelectField = ({ label, name, options, filterData, onChange }) => (
  <Box>
    <Typography sx={filterLabelSx}>{label}</Typography>
    <Select
      value={filterData[name] || ""}
      onChange={(e) => onChange(name, e.target.value)}
      displayEmpty
      renderValue={(selected) => {
        if (!selected) {
          return (
            <Typography
              component="span"
              sx={{
                display: "block",
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontSize: "0.85rem",
                color: tokens.colors.fontColour50,
              }}
            >
              Select
            </Typography>
          );
        }

        const selectedLabel = getSelectedOptionLabel(options, selected);

        return (
          <Box
            component="span"
            title={`${selectedLabel}`}
            sx={{
              display: "block",
              width: "100%",
              minWidth: 0,
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {selectedLabel}
          </Box>
        );
      }}
      endAdornment={
        filterData[name] ? (
          <InputAdornment
            position="end"
            sx={{
              position: "absolute",
              right: FILTER_FIELD_ICON_CONFIG.edgeOffset,
              height: "100%",
              m: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              size="small"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(name, "");
              }}
              sx={{
                p: 0,
                color: tokens.colors.fontColour85,
                width: FILTER_FIELD_ICON_CONFIG.clearSize,
                height: FILTER_FIELD_ICON_CONFIG.clearSize,
              }}
            >
              <ClearIcon sx={{ fontSize: FILTER_FIELD_ICON_CONFIG.clearSize }} />
            </IconButton>
          </InputAdornment>
        ) : null
      }
      sx={{
        ...filterSelectSx,
        width: "100%",
        "& .MuiSelect-select": {
          ...filterSelectSx["& .MuiSelect-select"],
          pr: `${getFilterIconReserve(Boolean(filterData[name]))}px !important`,
          boxSizing: "border-box",
          width: "100%",
          minWidth: 0,
          "& > *": {
            flex: "1 1 auto",
            minWidth: 0,
          },
        },
        "& .MuiSelect-icon": {
          right: filterData[name]
            ? getDropdownLeftOfClearOffset()
            : FILTER_FIELD_ICON_CONFIG.edgeOffset,
          fontSize: FILTER_FIELD_ICON_CONFIG.dropdownSize,
        },
      }}
      MenuProps={{ PaperProps: { sx: { maxHeight: 240, maxWidth: 280 } } }}
    >
      <MenuItem value="">
        <Typography sx={{ fontSize: "0.85rem", color: tokens.colors.fontColour50 }}>
          Select
        </Typography>
      </MenuItem>
      {options.map((opt) => {
        const optionValue = getFilterOptionValue(opt);
        const optionLabel = getFilterOptionLabel(opt);

        return (
          <MenuItem key={optionValue} value={optionValue}>
            {optionLabel}
          </MenuItem>
        );
      })}
    </Select>
  </Box>
);

const SECURITY_CHARGE_ALL = "ALL";

// Multi-select "Factored Securities" field that mirrors the order-entry
// BondCharacteristicsFields behaviour: an "ALL" entry (rendered without a
// checkbox) collapses the selection to ["ALL"], specific picks drop "ALL",
// tags are hidden, and a preview string is shown while the field is closed.
// Unlike order entry, the watchlist default is empty (shows "Select"); the
// user must explicitly pick "ALL" for the payload to carry the "ALL" string.
const MultiSelectField = ({ label, name, options, filterData, onChange }) => {
  const [open, setOpen] = useState(false);

  const baseOptions = Array.isArray(options) ? options : [];
  const allOptions = [
    SECURITY_CHARGE_ALL,
    ...baseOptions.filter((option) => option !== SECURITY_CHARGE_ALL),
  ];

  const rawValue = filterData[name];
  const selectedValues = Array.isArray(rawValue)
    ? rawValue
    : rawValue
    ? [rawValue]
    : [];

  const hasAllSelected = selectedValues.includes(SECURITY_CHARGE_ALL);
  const displayValues = hasAllSelected ? [SECURITY_CHARGE_ALL] : selectedValues;
  const previewValue = displayValues.join(", ");

  const handleMultiChange = (_event, newValue, reason, details) => {
    const changedOption = details?.option;
    const normalizedSelection = Array.from(
      new Set((newValue || []).map((value) => `${value}`.trim()).filter(Boolean))
    );

    let nextValues;
    if (reason === "clear") {
      nextValues = [];
    } else if (changedOption === SECURITY_CHARGE_ALL) {
      // Toggling the ALL row: select -> ["ALL"], unselect -> [] (empty/"Select").
      nextValues = normalizedSelection.includes(SECURITY_CHARGE_ALL)
        ? [SECURITY_CHARGE_ALL]
        : [];
    } else {
      nextValues = normalizedSelection.filter(
        (value) => value !== SECURITY_CHARGE_ALL
      );
    }

    onChange(name, nextValues);
  };

  return (
    <Box>
      <Typography sx={filterLabelSx}>{label}</Typography>
      <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        slotProps={{
          paper: {
            sx: {
              width: "max-content",
              minWidth: "100%",
              maxWidth: 280,
            },
          },
        }}
        options={allOptions}
        value={displayValues}
        openOnFocus
        onChange={handleMultiChange}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        isOptionEqualToValue={(option, value) => option === value}
        getOptionLabel={(option) => option || ""}
        renderOption={(props, option, { selected }) => {
          const hideCheckbox = option === SECURITY_CHARGE_ALL;

          return (
            <Box
              component="li"
              {...props}
              sx={{
                pl: "10px",
                py: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              {!hideCheckbox && (
                <Checkbox
                  checked={selected}
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                  disableRipple
                />
              )}
              <Typography
                sx={{
                  fontWeight: selected ? 700 : 400,
                  color: tokens.colors.fontColour,
                }}
              >
                {option}
              </Typography>
            </Box>
          );
        }}
        renderTags={() => null}
        renderInput={(params) => (
          <Box sx={{ position: "relative" }}>
            <TextField
              {...params}
              variant="standard"
              InputProps={{
                ...params.InputProps,
                disableUnderline: true,
                sx: { ...filterInputBaseSx, paddingRight: "44px !important" },
              }}
              sx={{ width: "100%" }}
            />
            {!open && (
              <Typography
                title={previewValue || undefined}
                sx={{
                  position: "absolute",
                  left: 10,
                  right: 44,
                  top: "50%",
                  transform: "translateY(-50%)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                  fontSize: "0.85rem",
                  color: previewValue
                    ? tokens.colors.fontColour
                    : tokens.colors.fontColour50,
                }}
              >
                {previewValue || "Select"}
              </Typography>
            )}
          </Box>
        )}
        sx={{
          width: "100%",
          "& .MuiInput-root.MuiAutocomplete-inputRoot": {
            ...filterInputBaseSx,
            paddingRight: "44px !important",
            flexWrap: "nowrap",
          },
          "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
            padding: "0 !important",
            minWidth: 0,
            color: open ? tokens.colors.fontColour : "transparent",
            caretColor: open ? tokens.colors.fontColour : "transparent",
          },
          "& .MuiAutocomplete-endAdornment": {
            top: "50%",
            transform: "translateY(-50%)",
            right: 4,
          },
        }}
      />
    </Box>
  );
};

const RadioField = ({ label, name, options, filterData, onChange }) => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <Typography sx={filterLabelSx}>{label}</Typography>
      {filterData[name] ? (
        <IconButton
          size="small"
          aria-label={`Clear ${label}`}
          onClick={() => onChange(name, "")}
          sx={{
            p: 0.25,
            mb: 0.5,
            color: tokens.colors.fontColour85,
            "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
          }}
        >
          <ClearIcon sx={{ fontSize: 15 }} />
        </IconButton>
      ) : null}
    </Box>
    <RadioGroup
      row
      value={filterData[name] || ""}
      onChange={(e) => onChange(name, e.target.value)}
      sx={{ gap: 0.5 }}
    >
      {options.map((opt) => {
        const value = typeof opt === "object" ? opt.value : opt;
        const optionLabel = typeof opt === "object" ? opt.label ?? opt.value : opt;

        return (
          <FormControlLabel
            key={value}
            value={value}
            control={<Radio size="small" />}
            label={<Typography sx={{ fontSize: "0.85rem" }}>{optionLabel}</Typography>}
            sx={{ mr: 1.5 }}
          />
        );
      })}
    </RadioGroup>
  </Box>
);

const DurationField = ({ filterData, onChange }) => (
  <Box>
    <Typography sx={filterLabelSx}>Duration</Typography>
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Select
        value={filterData.durationComparison || ""}
        onChange={(e) => {
          onChange("durationComparison", e.target.value);
          const operator =
            e.target.value === "greaterThanOrEqual" ? ">="
            : e.target.value === "lessThanOrEqual" ? "<="
            : "";
          const numericPart = filterData.duration?.replace(/[<>=]/g, "") || "";
          onChange("duration", numericPart ? `${operator}${numericPart}` : "");
        }}
        displayEmpty
        sx={{ ...filterSelectSx, width: 90, flexShrink: 0 }}
      >
        <MenuItem value="">Op</MenuItem>
        <MenuItem value="greaterThanOrEqual">{">="}</MenuItem>
        <MenuItem value="lessThanOrEqual">{"<="}</MenuItem>
      </Select>
      <NumericFormat
        customInput={TextField}
        variant="standard"
        thousandSeparator=","
        value={filterData.duration?.replace(/[<>=]/g, "") || ""}
        onValueChange={(values) => {
          const operator =
            filterData.durationComparison === "greaterThanOrEqual" ? ">="
            : filterData.durationComparison === "lessThanOrEqual" ? "<="
            : "";
          onChange("duration", values.floatValue ? `${operator}${values.floatValue}` : "");
        }}
        placeholder="Value"
        InputProps={{ disableUnderline: true, sx: filterInputBaseSx }}
        sx={{ flex: 1, minWidth: 0 }}
        disabled={!filterData.durationComparison}
        decimalScale={5}
        isAllowed={(values) => {
          const { floatValue } = values;
          return floatValue === undefined || (floatValue >= 0 && floatValue <= 50);
        }}
      />
    </Box>
  </Box>
);

const DateField = ({ label, name, filterData, onChange }) => (
  <Box>
    <Typography sx={filterLabelSx}>{label}</Typography>
    <TextField
      type="date"
      variant="standard"
      value={filterData[name] || ""}
      onChange={(e) => onChange(name, e.target.value)}
      InputProps={{ disableUnderline: true, sx: filterInputBaseSx }}
      sx={{ width: "100%" }}
    />
  </Box>
);

const renderFilterField = (
  field,
  filterData,
  onChange,
  {
    spRatingOptions = [],
    moodyRatingOptions = [],
    issuerOptions = [],
    paymentFrequencyOptions = [],
    sectorOptions = [],
    conventionMethodOptions = [],
    couponTypeOptions = [],
    currencyOptions = [],
    govStateOptions = [],
    incomeSourceOptions = [],
    issueCurrencyOptions = [],
    securityChargeOptions = [],
    subTypeOptions = [],
    rangeErrors = {},
  } = {}
) => {
  const label = WATCHLIST_FILTER_LABELS[field] || field;

  switch (field) {
    case "couponRange":
      return <NumericRangeField key={field} label={label} minName="couponMin" maxName="couponMax" filterData={filterData} onChange={onChange} error={rangeErrors.couponRange} />;
    case "price":
      return <NumericRangeField key={field} label={label} minName="priceMin" maxName="priceMax" filterData={filterData} onChange={onChange} error={rangeErrors.price} />;
    case "yield":
      return <NumericRangeField key={field} label={label} minName="yieldMin" maxName="yieldMax" filterData={filterData} onChange={onChange} error={rangeErrors.yield} />;
    case "maturityRanges":
      return <NumericRangeField key={field} label={label} minName="maturityMin" maxName="maturityMax" filterData={filterData} onChange={onChange} suffix="Yr" error={rangeErrors.maturityRanges} />;
    case "duration":
      return <DurationField key={field} filterData={filterData} onChange={onChange} />;
    case "callable":
    case "zeroCoupon":
    case "perpetual":
    case "insured":
    case "taxable":
    case "altMinTax":
      return <RadioField key={field} label={label} name={field} options={WATCHLIST_FILTER_FIELD_OPTIONS[field]} filterData={filterData} onChange={onChange} />;
    case "paymentFrequency":
      return <SelectField key={field} label={label} name={field} options={paymentFrequencyOptions} filterData={filterData} onChange={onChange} />;
    case "sector":
      return <SelectField key={field} label={label} name={field} options={sectorOptions} filterData={filterData} onChange={onChange} />;
    case "conventionMethod":
      return <SelectField key={field} label={label} name={field} options={conventionMethodOptions} filterData={filterData} onChange={onChange} />;
    case "couponType":
      return <SelectField key={field} label={label} name={field} options={couponTypeOptions} filterData={filterData} onChange={onChange} />;
    case "currency":
      return <SelectField key={field} label={label} name={field} options={currencyOptions} filterData={filterData} onChange={onChange} />;
    case "incomeSource":
      return <SelectField key={field} label={label} name={field} options={incomeSourceOptions} filterData={filterData} onChange={onChange} />;
    case "issueCurrency":
      return <SelectField key={field} label={label} name={field} options={issueCurrencyOptions} filterData={filterData} onChange={onChange} />;
    case "securityCharge":
      return <MultiSelectField key={field} label={label} name={field} options={securityChargeOptions} filterData={filterData} onChange={onChange} />;
    case "subType":
      return <SelectField key={field} label={label} name={field} options={subTypeOptions} filterData={filterData} onChange={onChange} />;
    case "state":
      return <SelectField key={field} label={label} name={field} options={govStateOptions} filterData={filterData} onChange={onChange} />;
    case "minimumRating":
      return <SelectField key={field} label={label} name={field} options={spRatingOptions} filterData={filterData} onChange={onChange} />;
    case "minimumRatingMoody":
      return <SelectField key={field} label={label} name={field} options={moodyRatingOptions} filterData={filterData} onChange={onChange} />;
    case "callStartDate":
    case "nextCallDate":
      return <DateField key={field} label={label} name={field} filterData={filterData} onChange={onChange} />;
    case "issuer": {
      const issuerAdornmentReserve = getFilterIconReserve(Boolean(filterData.issuer));

      return (
        <Box key={field}>
          <Typography sx={filterLabelSx}>{label}</Typography>
          <Autocomplete
            value={filterData.issuer || ""}
            inputValue={filterData.issuer || ""}
            slotProps={{
              paper: {
                sx: {
                  width: "max-content",
                  minWidth: "100%",
                  maxWidth: 280,
                },
              },
            }}
            onInputChange={(_, newValue) => onChange("issuer", newValue)}
            onChange={(_, newValue) => onChange("issuer", newValue || "")}
            options={issuerOptions}
            getOptionLabel={(option) => `${option ?? ""}`}
            isOptionEqualToValue={(option, value) => `${option ?? ""}` === `${value ?? ""}`}
            filterOptions={(options, state) => {
              const searchValue = `${state.inputValue || filterData.issuer || ""}`.trim();

              if (searchValue.length < WATCHLIST_ISSUER_MIN_SEARCH_LENGTH) {
                return [];
              }

              return watchlistIssuerFilterOptions(options, {
                ...state,
                inputValue: searchValue,
              });
            }}
            openOnFocus
            selectOnFocus
            clearOnBlur={false}
            handleHomeEndKeys
            noOptionsText={
              `${filterData.issuer || ""}`.trim().length < WATCHLIST_ISSUER_MIN_SEARCH_LENGTH
                ? "Type at least 2 characters"
                : "No issuers found"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                placeholder="Enter issuer"
                InputProps={{
                  ...params.InputProps,
                  disableUnderline: true,
                  sx: {
                    ...filterInputBaseSx,
                    paddingRight: "8px !important",
                    "& .MuiAutocomplete-input": {
                      padding: `0 ${issuerAdornmentReserve}px 0 0 !important`,
                      boxSizing: "border-box",
                      minWidth: "0 !important",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                    "& .MuiAutocomplete-endAdornment": {
                      right: FILTER_FIELD_ICON_CONFIG.edgeOffset,
                      display: "flex",
                      flexDirection: "row-reverse",
                      gap: `${FILTER_FIELD_ICON_CONFIG.gap}px`,
                    },
                    "& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator": {
                      p: 0,
                      m: 0,
                      width: FILTER_FIELD_ICON_CONFIG.clearSize,
                      height: FILTER_FIELD_ICON_CONFIG.clearSize,
                    },
                    "& .MuiAutocomplete-popupIndicator": {
                      width: FILTER_FIELD_ICON_CONFIG.dropdownSize,
                      height: FILTER_FIELD_ICON_CONFIG.dropdownSize,
                    },
                  },
                }}
                sx={{ width: "100%" }}
              />
            )}
            sx={{ width: "100%" }}
          />
        </Box>
      );
    }
    default:
      return null;
  }
};

const MAWatchlistFilterSection = ({
  filterData,
  appliedFilters = null,
  filterOptions,
  onFilterChange,
  onClearFilters,
  onClearFilterFields,
  disabled,
  clearDisabled = false,
  applyDisabled = false,
  onApplyFilters,
  heading = "Filters",
  isBondTypeRequired = false,
  forceExpanded = false,
  onForceExpandedResponded,
}) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (forceExpanded) {
      setExpanded(true);
      if (onForceExpandedResponded) {
        onForceExpandedResponded();
      }
    }
  }, [forceExpanded, onForceExpandedResponded]);

  const {
    spRatingOptions = [],
    moodyRatingOptions = [],
    issuerOptions = [],
    paymentFrequencyOptions = [],
    sectorOptions = [],
    conventionMethodOptions = [],
    couponTypeOptions = [],
    currencyOptions = [],
    govStateOptions = [],
    incomeSourceOptions = [],
    issueCurrencyOptions = [],
    securityChargeOptions = [],
    subTypeOptions = [],
  } = filterOptions || {};

  const bondType = filterData.bondType || "";
  const appliedBondType = useMemo(() => {
    if (!appliedFilters) {
      return "";
    }

    if (appliedFilters.bondType) {
      return appliedFilters.bondType;
    }

    return API_TYPE_TO_BOND_TYPE[appliedFilters.type] || appliedFilters.type || "";
  }, [appliedFilters]);
  const isBondTypeApplied = Boolean(bondType) && bondType === appliedBondType;
  const fields = WATCHLIST_FILTER_FIELDS[bondType] || [];

  const rangeErrors = useMemo(
    () => ({
      couponRange: getRangeError(filterData, "couponMin", "couponMax"),
      maturityRanges: getRangeError(filterData, "maturityMin", "maturityMax"),
      price: getRangeError(filterData, "priceMin", "priceMax"),
      yield: getRangeError(filterData, "yieldMin", "yieldMax"),
    }),
    [filterData]
  );
  const hasRangeError = fields.some((f) => rangeErrors[f]);

  const handleChange = (name, value) => {
    onFilterChange(name, value);
  };

  const handleBondTypeChange = (e) => {
    const nextBondType = e.target.value;

    if (!nextBondType) {
      if (isBondTypeRequired) {
        return;
      }

      onClearFilters?.();
      return;
    }

    onFilterChange("bondType", nextBondType);
  };

  const handleBondTypeClear = (event) => {
    if (isBondTypeRequired) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    onClearFilters?.();
  };

  // Build chips from applied filters only; draft filter edits appear after Apply.
  const chipsInput = useMemo(() => {
    const payload = appliedFilters ? { ...appliedFilters } : null;
    if (!payload) {
      return null;
    }
    if (payload.type) {
      payload.type = API_TYPE_TO_BOND_TYPE[payload.type] || payload.type;
    }
    // Remove empty values
    const cleaned = {};
    Object.entries(payload).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") {
        cleaned[k] = v;
      }
    });
    return Object.keys(cleaned).length > 0
      ? {
          ...cleaned,
          source: "ma-watchlist",
        }
      : null;
  }, [appliedFilters]);

  const handleChipDelete = (key) => {
    const clearFields = (fields) => {
      const normalizedFields = Array.from(new Set((fields || []).filter(Boolean)));

      if (normalizedFields.length === 0) {
        return;
      }

      if (onClearFilterFields) {
        onClearFilterFields(normalizedFields);
        return;
      }

      normalizedFields.forEach((fieldName) => onFilterChange(fieldName, ""));
    };

    if (key === "type" || key === "bondType") {
      if (isBondTypeRequired) {
        return;
      }

      onClearFilters?.();
      return;
    }

    // Map API keys back to store keys for deletion
    const apiToStoreMap = {
      type: ["bondType"],
      minCoupon: ["couponMin"],
      maxCoupon: ["couponMax"],
      minMaturity: ["maturityMin"],
      maxMaturity: ["maturityMax"],
      minPrice: ["priceMin"],
      maxPrice: ["priceMax"],
      minYtm: ["yieldMin"],
      maxYtm: ["yieldMax"],
      minDuration: ["duration", "durationComparison"],
      maxDuration: ["duration", "durationComparison"],
      minRatingSP: ["minimumRating"],
      maxRatingSP: ["minimumRating"],
      minRatingMoody: ["minimumRatingMoody"],
      maxRatingMoody: ["minimumRatingMoody"],
    };

    if (key.startsWith("min") || key.startsWith("max")) {
      const baseKey = key.replace(/^(min|max)/, "");
      const minStoreFields = apiToStoreMap[`min${baseKey}`] || [];
      const maxStoreFields = apiToStoreMap[`max${baseKey}`] || [];
      const fieldsToClear = [...minStoreFields, ...maxStoreFields];

      if (fieldsToClear.length > 0) {
        clearFields(fieldsToClear);
        return;
      }

      // If not in map, try direct clear
      clearFields([key]);
    } else {
      const fieldsToClear = apiToStoreMap[key] || [key];
      clearFields(fieldsToClear);
    }
  };

  return (
    <Box
      sx={{
        border: `1px solid ${tokens.colors.bodyBorderColor50}`,
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: tokens.colors.white,
        flexShrink: 0,
      }}
    >
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: "8px",
          py: 1,
          cursor: "pointer",
          backgroundColor: tokens.colors.panelTitleBackgroundColor,
          userSelect: "none",
          "&:hover": {
            backgroundColor: tokens.colors.defaultGridHoverColor,
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
          <FilterListIcon sx={{ fontSize: "1.1rem", color: tokens.colors.fontColour85, flexShrink: 0 }} />
          <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: tokens.colors.fontColour, flexShrink: 0 }}>
            {heading}
          </Typography>
          <Select
            value={bondType}
            onChange={handleBondTypeChange}
            onClick={(e) => e.stopPropagation()}
            displayEmpty
            size="small"
            endAdornment={
              isBondTypeApplied && !isBondTypeRequired ? (
                <InputAdornment
                  position="end"
                  sx={{
                    position: "absolute",
                    right: FILTER_FIELD_ICON_CONFIG.edgeOffset,
                    mr: 0,
                    ml: 0,
                    pr: 0,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <IconButton
                    size="small"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={handleBondTypeClear}
                    sx={{
                      p: 0,
                      m: 0,
                      minWidth: FILTER_FIELD_ICON_CONFIG.clearSize,
                      width: FILTER_FIELD_ICON_CONFIG.clearSize,
                      height: FILTER_FIELD_ICON_CONFIG.clearSize,
                      borderRadius: 0,
                      color: tokens.colors.fontColour85,
                    }}
                  >
                    <ClearIcon sx={{ fontSize: FILTER_FIELD_ICON_CONFIG.clearSize }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
            renderValue={(selected) =>
              selected ? (
                <Box
                  component="span"
                  title={selected}
                  sx={{
                    display: "block",
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {selected}
                </Box>
              ) : (
                <Typography
                  component="span"
                  sx={{
                    display: "block",
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "0.82rem",
                    color: tokens.colors.fontColour50,
                  }}
                >
                  Select type
                </Typography>
              )
            }
            sx={{
              width: 164,
              minWidth: 164,
              flexShrink: 0,
              height: 30,
              fontSize: "0.82rem",
              borderRadius: "8px",
              backgroundColor: tokens.colors.white,
              "& .MuiSelect-select": {
                py: "4px",
                pl: 1,
                pr: isBondTypeApplied && !isBondTypeRequired
                  ? `${getFilterIconReserve(true)}px !important`
                  : `${getFilterIconReserve(false)}px !important`,
                boxSizing: "border-box",
                width: "100%",
                minWidth: 0,
                "& > *": {
                  flex: "1 1 auto",
                  minWidth: 0,
                },
              },
              "& .MuiSelect-icon": {
                right: isBondTypeApplied && !isBondTypeRequired
                  ? getDropdownLeftOfClearOffset()
                  : FILTER_FIELD_ICON_CONFIG.edgeOffset,
                fontSize: FILTER_FIELD_ICON_CONFIG.dropdownSize,
                p: 0,
                m: 0,
              },
            }}
            disabled={disabled}
          >
            {!isBondTypeRequired ? (
              <MenuItem value="">
                <Typography sx={{ fontSize: "0.82rem", color: tokens.colors.fontColour50 }}>
                  Select type
                </Typography>
              </MenuItem>
            ) : null}
            {WATCHLIST_FILTER_BOND_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
          {!expanded && chipsInput && (
            <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, ml: 0.5 }}>
              <FilterChips
                input={chipsInput}
                onDeleteChip={handleChipDelete}
                onClearFilters={onClearFilters}
                maxVisibleChips={3}
                // readOnly
                compact
              />
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          {onClearFilters && (
            <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onClearFilters();
              }}
              disabled={disabled || clearDisabled}
              sx={{
                borderRadius: "8px",
                minHeight: 28,
                textTransform: "none",
                minWidth: "auto",
                px: 1,
                py: 0.25,
                fontSize: "0.82rem",
                color: tokens.palette.primary,
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "transparent",
                  // textDecoration: "underline",
                },
                "&.Mui-disabled": {
                  color: tokens.colors.fontColour50,
                  // borderColor: "transparent",
                },
              }}
            >
              Reset All
            </Button>
          )}
          {onApplyFilters && (
            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onApplyFilters();
              }}
              disabled={disabled || applyDisabled || hasRangeError}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "8px",
                px: 1,
                py: 0.25,
                fontSize: "0.82rem",
                minHeight: 28,
                backgroundColor: tokens.palette.primary,
                color: tokens.colors.white,
                "&:hover": {
                  backgroundColor: tokens.palette.primaryA80,
                },
                "&.Mui-disabled": {
                  backgroundColor: tokens.colors.defaultGridRowColor,
                  color: tokens.colors.fontColour50,
                  border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                },
              }}
            >
              Apply
            </Button>
          )}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((prev) => !prev);
            }}
            sx={{ color: tokens.colors.fontColour }}
          >
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box
          sx={{
            maxHeight: FILTER_COLLAPSED_MAX_HEIGHT,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {!bondType ? (
            <Box sx={{ p: 2, pt: 1.5 }}>
              <Typography sx={{ fontSize: "0.85rem", color: tokens.colors.fontColour85 }}>
                Select a type to view available watchlist filters.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 1.5,
                columnGap: 1.5,
                p: 2,
                pt: 1.5,
                "& > *": {
                  minWidth: 0,
                },
              }}
            >
              {fields.map((field) =>
                renderFilterField(field, filterData, handleChange, {
                  spRatingOptions,
                  moodyRatingOptions,
                  issuerOptions,
                  paymentFrequencyOptions,
                  sectorOptions,
                  conventionMethodOptions,
                  couponTypeOptions,
                  currencyOptions,
                  govStateOptions,
                  incomeSourceOptions,
                  issueCurrencyOptions,
                  securityChargeOptions,
                  subTypeOptions,
                  rangeErrors,
                })
              )}
            </Box>
          )}
        </Box>

      </Collapse>
    </Box>
  );
};

export default MAWatchlistFilterSection;
